import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Layout } from './components/layout/Layout';
import { ResponsiveRoute } from './components/ResponsiveRoute';
import { DashboardPage } from './pages/Dashboard';
import { DashboardV3Page } from './pages/DashboardV3';
import { MarketPage } from './pages/Market';
import { KalenderPage } from './pages/Kalender';
import { SentimenPage } from './pages/Sentimen';
import { LikuidasiPage } from './pages/Likuidasi';
import { PositionsPage } from './pages/Positions';
import { CandidatesPage } from './pages/Candidates';
import { HistoryPage } from './pages/History';
import { ActivityPage } from './pages/Activity';
import { AtAnalysisPage } from './pages/AtAnalysis';
import { DcdExplorerPage } from './pages/DcdExplorer';
import { SettingsPage } from './pages/Settings';
import { LoginPage } from './pages/Login';
import { MobileDashboardPage } from './components/mobile/MobileDashboard';
import { MobileMarketPage } from './components/mobile/MobileMarket';
import { MobileCandidatesPage } from './components/mobile/MobileCandidates';
import { MobilePositionsPage } from './components/mobile/MobilePositions';
import { MobileSettingsPage } from './components/mobile/MobileSettings';
import { AuthContext, useAuthProvider } from './hooks/use-auth';
import { getStoredToken, redirectToLogin } from './utils/token-storage';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      staleTime: 30_000,
      refetchOnWindowFocus: false,
    },
  },
});

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const token = getStoredToken();
  if (!token) {
    redirectToLogin(window.location.href);
    return null;
  }
  return <>{children}</>;
}

function LoadingScreen() {
  return (
    <div style={{
      display: 'grid',
      placeItems: 'center',
      height: '100vh',
      background: 'var(--bg-base)',
      color: 'var(--text-muted)',
    }}>
      Loading...
    </div>
  );
}

export function App() {
  const auth = useAuthProvider();

  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <AuthContext.Provider value={auth}>
          {auth.loading ? (
            <LoadingScreen />
          ) : (
            <Routes>
              <Route path="/login" element={<LoginPage />} />
              {/* 1 Layout responsive — auto-switch mobile/desktop */}
              <Route
                path="/"
                element={
                  <ProtectedRoute>
                    <Layout />
                  </ProtectedRoute>
                }
              >
                <Route index element={
                  <ResponsiveRoute
                    mobile={<MobileDashboardPage />}
                    desktop={<DashboardPage />}
                  />
                } />
                <Route path="market" element={
                  <ResponsiveRoute
                    mobile={<MobileMarketPage />}
                    desktop={<MarketPage />}
                  />
                } />
                <Route path="kalender" element={<KalenderPage />} />
                <Route path="sentimen" element={<SentimenPage />} />
                <Route path="likuidasi" element={<LikuidasiPage />} />
                <Route path="dcd-explorer" element={<DcdExplorerPage />} />
                <Route path="positions" element={
                  <ResponsiveRoute
                    mobile={<MobilePositionsPage />}
                    desktop={<PositionsPage />}
                  />
                } />
                <Route path="candidates" element={
                  <ResponsiveRoute
                    mobile={<MobileCandidatesPage />}
                    desktop={<CandidatesPage />}
                  />
                } />
                <Route path="history" element={<HistoryPage />} />
                <Route path="portfolio" element={<Navigate to="/candidates" replace />} />
                <Route path="activity" element={<ActivityPage />} />
                <Route path="atr-analysis" element={<AtAnalysisPage />} />
                <Route path="settings" element={
                  <ResponsiveRoute
                    mobile={<MobileSettingsPage />}
                    desktop={<SettingsPage />}
                  />
                } />
                <Route path="dashboard-v3" element={<DashboardV3Page />} />
              </Route>
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          )}
        </AuthContext.Provider>
      </BrowserRouter>
    </QueryClientProvider>
  );
}
